import produce from 'immer';
import { AnyAction } from 'redux';
import { ActionsObservable, combineEpics, ofType, StateObservable } from 'redux-observable';
import { of } from 'rxjs';
import { ajax, AjaxError, AjaxResponse } from 'rxjs/ajax';
import { take, catchError, delay, map, retry, retryWhen, switchMap } from 'rxjs/operators';
import {
  FOUND_PAYOFF_DEBT,
  ERROR_PAYOFF_DEBT,
  EDITED_PAYOFF_DEBT,
  ERROR_EDIT_PAYOFF_DEBT,
  FOUND_USER_INFO,
  EDIT_PAYOFF_DEBT,
} from './actions';
import config from '../config';

export type PayoffDebt = {
  id?: number;
  name: string;
  current_balance: number;
  interest_rate: number;
  minimum_payment: number;
  status: string;
};

// Reducer
export type PayoffDebtState = {
  loading: boolean;
  data: PayoffDebt[];
  error: AjaxError;
};

const defaultState: PayoffDebtState = {
  loading: false,
  data: [],
  error: {} as AjaxError,
};

export const payoffDebtReducer = (state = defaultState, action: AnyAction): PayoffDebtState =>
  produce(state, (draftState) => {
    switch (action.type) {
      case FOUND_PAYOFF_DEBT:
      case EDITED_PAYOFF_DEBT:{
        draftState.data = action.debts;
        return;
      }
    }
  });

const getPortalDebts$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(FOUND_USER_INFO),
    switchMap(() => {
      const url = `${config.apiUrl}/portalDebts`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = {
        url,
        headers,
        method: 'GET',
        responseType: 'json',
        withCredentials: true,
      };
      return ajax(ajaxRequest).pipe(
        retryWhen(errors =>
          errors.pipe(
            delay(500),
            take(1)
          )
        ),
        map((res: AjaxResponse) => ({ type: FOUND_PAYOFF_DEBT, debts: res.response.debts })),
        catchError((error: AjaxError) => {
          // Silently handle errors for new users without debts
          // Log to console for debugging but don't show blocking errors to users
          console.log('Portal debts not found - user may be new without debts:', error);
          
          // Return empty debts array instead of error action to prevent blocking UI
          return of({ type: FOUND_PAYOFF_DEBT, debts: [] });
        })
      );
    })
  );

const editPortalDebts$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(EDIT_PAYOFF_DEBT),
    switchMap((action) => {
      const url = `${config.apiUrl}/portalDebts`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}`, 'Content-Type': 'application/json' };
      const body = { debts: action.debts };
      const ajaxRequest = {
        url,
        headers,
        body,
        method: 'PUT',
        responseType: 'json',
        withCredentials: true,
      };
      return ajax(ajaxRequest);
    }),
    map((res: AjaxResponse) => ({ type: EDITED_PAYOFF_DEBT, debts: res.response })),
    catchError((error: AjaxError) => {
      // Silently handle errors for new users without debts
      // Log to console for debugging but don't show blocking errors to users
      console.log('Failed to update portal debts - user may be new without debts:', error);
      
      // Return empty debts array instead of error action to prevent blocking UI
      return of({ type: EDITED_PAYOFF_DEBT, debts: [] });
    })
  );

export const payoffDebtEpic = combineEpics(
  getPortalDebts$,
  editPortalDebts$
);